import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isFunction from 'shared-utils/src/checkVarType/isFunction';
import isValidString from 'shared-utils/src/checkVarType/isValidString';
import createStyle from 'shared-utils/src/createStyle';
import makeClassName from 'shared-utils/src/makeClassName';
import ListCheckboxItem from '../ListCheckboxItem';
import styles, { mainCls, clsItem, clsNested } from './styles';

const useStyles = createStyle(styles);

const getCheckedItems = (items, result = []) => {
    items.forEach(item => {
        item.checked && result.push(item);
        item.children && getCheckedItems(item.children, result);
    })
    return result;
}

const setItemChecked = (item, status = false) => {
    const { children } = item;
    item.checked = status;

    if (children) {
        item.open = status;
        if (status) {
            item.indeterminate = false;
        }
        children.forEach(i => setItemChecked(i, status));
    }
}

export default function ListCheckbox({
    className,
    classNameItem,
    items,
    onChange,
    onAccordion,
    label,
    containerTouchStart,
    checkedColor,
    checkboxGutters,
}) {
    const classes = useStyles();
    const [sItems, setItems] = useState([]);
    useEffect(() => setItems(items), [items]);
    const handleItemChange = (chk, opts, item) => {
        setItemChecked(item, chk);
        onChange(sItems, {
            ...opts,
            item,
            checked: chk,
            checkedItems: getCheckedItems(sItems),
            items: sItems,
        });
        item.children && setItems([...sItems]);
    };
    const handleChange = (inItems, opts, parent) => {
        parent.checked = inItems.every(i => i.checked);
        parent.indeterminate = !parent.checked && inItems.some(i => i.checked);
        onChange(sItems, {
            ...opts,
            items: inItems,
            checkedItems: getCheckedItems(sItems),
        });
        setItems([...sItems]);
    };
    const onToggle = (val, opts, item) => {
        item.open = val;
        onAccordion(val, opts, item);
    };
    const clsMain = makeClassName([
        classes[mainCls],
        className,
    ])

    return <div className={clsMain} onTouchStart={containerTouchStart}>
        {
            sItems.map(item => (
                <ListCheckboxItem
                    className={makeClassName([clsItem, classNameItem])}
                    key={item.key || item.label}
                    label={isFunction(label) ? label({ item }) : item.label }
                    checked={!!item.checked}
                    indeterminate={!!item.indeterminate}
                    accordion={!!item.children}
                    open={!!item.open}
                    onChange={(chk, opts) => handleItemChange(chk, opts, item)}
                    onAccordion={(val, opts) => onToggle(val, opts, item)}
                    forDescription={item.lvlHasDescription}
                    withDescription={item.lvlHasDescription && isValidString(item.description)}
                    checkedColor={checkedColor}
                    checkboxGutters={checkboxGutters}
                >
                    {
                        !!item.children &&
                        <ListCheckbox
                            className={clsNested}
                            items={item.children}
                            label={label}
                            onChange={(chk, opts) => handleChange(chk, opts, item)}
                            checkedColor={checkedColor}
                        />
                    }
                </ListCheckboxItem>
            ))
        }
    </div>;
};

ListCheckbox.propTypes = {
    /**
     * Component class
     */
    className: PropTypes.string,

    /**
     * Component class
     */
    classNameItem: PropTypes.string,

    /**
     * Array containing checkbox list to render, eg.
     * `[{ label: 'T 1', children: [{ label: 'T 11'}]}, { label: 'T 2', checked: true}]`
     */
    items: PropTypes.array,

    /**
     * Function which renders a custom label, eg.
     * `({ item }) => <div>{ item.label }</div>`
     */
    label: PropTypes.func,

    /**
     * The callback triggers on the update of any changed checkbox, eg.
     * `(items, { checkedItems }) => console.log({ items, checkedItems })`
     */
    onChange: PropTypes.func,

    onAccordion: PropTypes.func,
    containerTouchStart: PropTypes.func,
    checkedColor: PropTypes.string,
    checkboxGutters: PropTypes.string,
};

ListCheckbox.defaultProps = {
    className: '',
    classNameItem: '',
    items: [],
    label: null,
    onChange: () => {},
    onAccordion: () => {},
    containerTouchStart: () => {},
    checkedColor: '',
    checkboxGutters: 'l',
};